import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { ArrowLeft, Users, MessageSquare, Calendar, TrendingUp, MapPin, Loader, BarChart3, Download, Ticket, Plus, Copy, Check, Hourglass, DoorOpen } from "lucide-react";
import UsersListPanel from "@/components/UsersListPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, sub, color = "#E48D57" }) {
  return (
    <div className="bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon size={16} style={{ color }} />
        </div>
        <span className="text-xs text-[#5B3E2E]/50 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-heading font-bold text-[#5B3E2E]">{value}</p>
      {sub && <p className="text-xs text-[#5B3E2E]/40 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoCodes, setPromoCodes] = useState([]);
  const [generatingPromo, setGeneratingPromo] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const isPrimaryAdmin = user?.role === "admin";

  useEffect(() => {
    const load = () => {
      axios.get(`${API}/admin/analytics`, { withCredentials: true })
        .then(r => { setData(r.data); setRefreshedAt(new Date()); })
        .catch(() => {})
        .finally(() => setLoading(false));
    };
    load();
    // Refresh hourly while dashboard is open (and on focus)
    const interval = setInterval(load, 60 * 60 * 1000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    if (isPrimaryAdmin) loadPromoCodes();
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [isPrimaryAdmin]);

  const loadPromoCodes = async () => {
    try {
      const r = await axios.get(`${API}/admin/promo-codes`, { withCredentials: true });
      setPromoCodes(r.data || []);
    } catch (_) {}
  };

  const handleGeneratePromo = async () => {
    setGeneratingPromo(true);
    try {
      await axios.post(`${API}/admin/promo-codes/generate`, {}, { withCredentials: true });
      await loadPromoCodes();
    } catch (_) {} finally {
      setGeneratingPromo(false);
    }
  };

  const handleGenerateLifetime = async () => {
    const label = window.prompt("Etiqueta opcional (para recordar a quién se lo diste — ej. 'Familia', 'Amigos beta'). Deja vacío si prefieres.");
    if (label === null) return;  // canceló
    const maxUsesStr = window.prompt("¿Cuántos usos máximos? (1-1000, por defecto 100)");
    if (maxUsesStr === null) return;
    const maxUses = parseInt(maxUsesStr, 10) || 100;
    setGeneratingPromo(true);
    try {
      await axios.post(`${API}/admin/promo-codes/generate-lifetime`, { label: label.trim(), max_uses: maxUses }, { withCredentials: true });
      await loadPromoCodes();
    } catch (err) {
      alert(err.response?.data?.detail || "No se pudo generar el código");
    } finally {
      setGeneratingPromo(false);
    }
  };

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const r = await axios.get(`${API}/admin/users/export`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "usuarios_refugium.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (_) {} finally {
      setExportingCsv(false);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1500);
    });
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return iso; }
  };

  if (user?.role !== "admin" && user?.role !== "secondary_admin") {
    return <div className="max-w-lg mx-auto px-4 py-12 text-center"><p className="text-[#5B3E2E]/60">Sin acceso</p></div>;
  }

  if (loading) {
    return <div className="flex justify-center py-20"><Loader size={24} className="text-[#E48D57] animate-spin" /></div>;
  }

  if (!data) {
    return <div className="max-w-lg mx-auto px-4 py-12 text-center"><p className="text-[#5B3E2E]/60">Error cargando datos</p></div>;
  }

  const maxTopic = data.topics.length > 0 ? Math.max(...data.topics.map(t => t.count)) : 1;

  return (
    <div data-testid="admin-dashboard" className="max-w-2xl mx-auto px-4 py-6">
      <button data-testid="dashboard-back" onClick={() => navigate("/app/admin")} className="flex items-center gap-2 text-[#5B3E2E]/60 hover:text-[#5B3E2E] mb-6">
        <ArrowLeft size={18} />
        <span className="text-sm">Volver al panel</span>
      </button>

      <div className="flex items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="font-heading text-2xl text-[#5B3E2E] font-bold">Dashboard</h1>
          {refreshedAt && (
            <p className="text-[10px] text-[#5B3E2E]/40 mt-0.5">Actualizado {refreshedAt.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })}</p>
          )}
        </div>
        <button
          data-testid="export-csv-btn"
          onClick={handleExportCsv}
          disabled={exportingCsv}
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold bg-[#6B9070] text-white hover:bg-[#5A8060] disabled:opacity-60 transition"
        >
          {exportingCsv ? <Loader size={14} className="animate-spin" /> : <Download size={14} />}
          <span>Exportar CSV</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <StatCard icon={Users} label="Usuarios" value={data.users.total} sub={`+${data.users.this_week} esta semana`} color="#6B9070" />
        <StatCard icon={TrendingUp} label="Conversión" value={`${data.users.conversion_rate}%`} sub={`${data.users.paid} pagados`} color="#E48D57" />
        <StatCard icon={MessageSquare} label="Salas" value={data.rooms.total} sub={`${data.rooms.active} activas`} color="#6B9070" />
        <StatCard icon={Calendar} label="Eventos" value={data.events.total} color="#F0A82C" />
      </div>

      {/* Sala health: waiting + empty */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <StatCard
          icon={Hourglass}
          label="En espera"
          value={data.rooms.waiting_acompanantes ?? 0}
          sub="acompañantes esperando"
          color="#E48D57"
        />
        <StatCard
          icon={DoorOpen}
          label="Salas vacías"
          value={data.rooms.empty_rooms ?? 0}
          sub="sin acompañantes"
          color="#5B3E2E"
        />
      </div>

      {/* User breakdown */}
      <div className="bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 p-4 mb-4">
        <p className="text-xs tracking-[0.15em] uppercase text-[#5B3E2E]/50 font-semibold mb-3">Desglose de usuarios</p>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-3 bg-[#6B9070]/5 rounded-xl">
            <p className="text-lg font-bold text-[#6B9070]">{data.users.paid}</p>
            <p className="text-[10px] text-[#5B3E2E]/50">Pagados</p>
          </div>
          <div className="p-3 bg-[#F0A82C]/5 rounded-xl">
            <p className="text-lg font-bold text-[#F0A82C]">{data.users.trial}</p>
            <p className="text-[10px] text-[#5B3E2E]/50">En trial</p>
          </div>
          <div className="p-3 bg-[#5B3E2E]/5 rounded-xl">
            <p className="text-lg font-bold text-[#5B3E2E]">{data.messages.total}</p>
            <p className="text-[10px] text-[#5B3E2E]/50">Mensajes</p>
          </div>
        </div>
      </div>

      {/* Promo Codes - solo admin principal */}
      {isPrimaryAdmin && (
        <div data-testid="promo-codes-section" className="bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 p-4 mb-4">
          <div className="flex items-center justify-between mb-3 gap-2">
            <div className="flex items-center gap-2">
              <Ticket size={16} className="text-[#E48D57]" />
              <p className="text-xs tracking-[0.15em] uppercase text-[#5B3E2E]/50 font-semibold">Códigos promocionales</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="generate-lifetime-btn"
                onClick={handleGenerateLifetime}
                disabled={generatingPromo}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#6B9070] text-white hover:bg-[#5a7c60] disabled:opacity-60 transition"
                title="Código de acceso gratuito indefinido — para amigos y familia"
              >
                {generatingPromo ? <Loader size={12} className="animate-spin" /> : <Plus size={12} />}
                <span>Vitalicio</span>
              </button>
              <button
                data-testid="generate-promo-btn"
                onClick={handleGeneratePromo}
                disabled={generatingPromo}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#E48D57] text-white hover:bg-[#c46724] disabled:opacity-60 transition"
                title="Código de prueba — 7 días, 10 usos máx."
              >
                {generatingPromo ? <Loader size={12} className="animate-spin" /> : <Plus size={12} />}
                <span>Prueba</span>
              </button>
            </div>
          </div>
          <p className="text-[11px] text-[#5B3E2E]/50 mb-3">
            <span className="font-semibold text-[#6B9070]">Vitalicio</span>: acceso gratuito indefinido (para regalar a amigos/familia). ·{" "}
            <span className="font-semibold text-[#E48D57]">Prueba</span>: 7 días, 10 usos máx.
          </p>

          {promoCodes.length === 0 ? (
            <p className="text-sm text-[#5B3E2E]/40 text-center py-4">Sin códigos generados aún</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {promoCodes.map((p) => {
                const isLifetime = p.kind === "lifetime";
                const expired = !isLifetime && !p.active;
                const exhausted = (p.current_uses || 0) >= (p.max_uses || 10);
                return (
                  <div key={p.code} data-testid="promo-code-item" className="flex items-center justify-between gap-2 p-2 bg-white border border-[#5B3E2E]/5 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-mono text-sm font-bold text-[#5B3E2E] tracking-wider">{p.code}</p>
                        {isLifetime ? (
                          <span className="text-[9px] font-bold tracking-wider bg-[#6B9070]/15 text-[#6B9070] px-1.5 py-0.5 rounded uppercase">Vitalicio</span>
                        ) : (
                          <span className="text-[9px] font-bold tracking-wider bg-[#E48D57]/12 text-[#E48D57] px-1.5 py-0.5 rounded uppercase">Prueba</span>
                        )}
                      </div>
                      <p className="text-[10px] text-[#5B3E2E]/50">
                        {isLifetime ? "Sin caducidad" : `Expira ${formatDate(p.expires_at)}`}
                        {" · "}{p.current_uses}/{p.max_uses} usos
                        {p.label && <span className="text-[#5B3E2E]/70"> · {p.label}</span>}
                        {expired && <span className="text-[#c44a3a] ml-1">· Expirado</span>}
                        {exhausted && <span className="text-[#c44a3a] ml-1">· Agotado</span>}
                      </p>
                    </div>
                    <button
                      onClick={() => handleCopyCode(p.code)}
                      className="p-2 rounded-lg hover:bg-[#5B3E2E]/5 text-[#5B3E2E]/60"
                      aria-label="Copiar código"
                    >
                      {copiedCode === p.code ? <Check size={14} className="text-[#6B9070]" /> : <Copy size={14} />}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Topics */}
      <div className="bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 size={16} className="text-[#E48D57]" />
          <p className="text-xs tracking-[0.15em] uppercase text-[#5B3E2E]/50 font-semibold">Temáticas más activas</p>
        </div>
        {data.topics.length === 0 ? (
          <p className="text-sm text-[#5B3E2E]/40 text-center py-4">Sin datos aún</p>
        ) : (
          <div className="space-y-2">
            {data.topics.map((t, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs text-[#5B3E2E]/70 w-28 truncate">{t.name}</span>
                <div className="flex-1 h-5 bg-[#5B3E2E]/5 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${(t.count / maxTopic) * 100}%`, backgroundColor: i % 2 === 0 ? '#6B9070' : '#E48D57' }}
                  />
                </div>
                <span className="text-xs text-[#5B3E2E]/50 w-6 text-right">{t.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Geography */}
      <div className="bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <MapPin size={16} className="text-[#6B9070]" />
          <p className="text-xs tracking-[0.15em] uppercase text-[#5B3E2E]/50 font-semibold">Ubicación de usuarios</p>
        </div>
        {data.geography.length === 0 ? (
          <p className="text-sm text-[#5B3E2E]/40 text-center py-4">Sin datos de ubicación aún</p>
        ) : (
          <div className="space-y-2">
            {data.geography.map((g, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-[#5B3E2E]/3 rounded-lg">
                <div>
                  <span className="text-sm text-[#5B3E2E] font-medium">{g.country}</span>
                  {g.city && <span className="text-xs text-[#5B3E2E]/50 ml-2">{g.city}</span>}
                </div>
                <span className="text-sm font-semibold text-[#E48D57]">{g.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Daily registrations */}
      {data.daily_registrations.length > 0 && (
        <div className="bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 p-4">
          <p className="text-xs tracking-[0.15em] uppercase text-[#5B3E2E]/50 font-semibold mb-3">Registros últimos 30 días</p>
          <div className="flex items-end gap-1 h-24">
            {data.daily_registrations.map((d, i) => {
              const max = Math.max(...data.daily_registrations.map(r => r.count));
              const h = max > 0 ? (d.count / max) * 100 : 0;
              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${d.count}`}>
                  <div className="w-full bg-[#E48D57] rounded-t-sm min-h-[2px]" style={{ height: `${Math.max(h, 4)}%` }} />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-[9px] text-[#5B3E2E]/30 mt-1">
            <span>{data.daily_registrations[0]?.date?.slice(5)}</span>
            <span>{data.daily_registrations[data.daily_registrations.length - 1]?.date?.slice(5)}</span>
          </div>
        </div>
      )}

      <UsersListPanel isPrimaryAdmin={isPrimaryAdmin} />
    </div>
  );
}
